import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFieldDefinition,
  deleteFieldDefinition,
  listFieldDefinitions,
  reorderFieldDefinitions,
  updateFieldDefinition,
  type FieldDefinitionInput,
} from '@/api/fields'

/** Every definition for both entity types — the admin list on the settings page. */
export function useAllFieldDefinitions() {
  // Field definitions change rarely; keep them fresh for a while so the "Manage fields" overlay
  // opens from cache instead of refetching (and flashing empty) each time.
  return useQuery({ queryKey: ['fields', 'all'], queryFn: () => listFieldDefinitions(), staleTime: 5 * 60_000 })
}

// Mutations invalidate the whole ['fields'] prefix so both the admin list and the
// per-entity lists used by contact/deal forms refresh.

export function useCreateFieldDefinition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: FieldDefinitionInput) => createFieldDefinition(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fields'] }),
  })
}

export function useUpdateFieldDefinition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: FieldDefinitionInput }) =>
      updateFieldDefinition(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fields'] }),
  })
}

export function useDeleteFieldDefinition() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteFieldDefinition(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fields'] }),
  })
}

export function useReorderFieldDefinitions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ entityType, ids }: { entityType: 'contact' | 'deal'; ids: string[] }) =>
      reorderFieldDefinitions(entityType, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fields'] }),
  })
}
